/**
 * Saved Palettes Management
 * Handles Firebase operations for user's saved palettes
 */

import { getDatabase, ref, set, get, remove, push, update } from 'firebase/database';
import { encodePaletteForShare, decodePaletteFromShare } from '../utils/paletteCode';

const MAX_PALETTES_PER_USER = 40;

/**
 * Save a palette to user's Firebase account
 *
 * @param {string} userId - User's Firebase UID
 * @param {Array} palette - Array of marker objects
 * @param {string} name - Palette name (max 50 chars)
 * @returns {Promise<{success: boolean, paletteId?: string, error?: string}>}
 */
export async function savePalette(userId, palette, name) {
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }

  if (!palette || !Array.isArray(palette) || palette.length === 0) {
    return { success: false, error: 'Invalid palette: must contain at least one marker' };
  }

  if (palette.length > 20) {
    return { success: false, error: `This palette has ${palette.length} markers, but the maximum is 20. Try the "Convert & Save" option to match it to your collection instead.` };
  }

  if (!name || typeof name !== 'string' || name.trim().length === 0) {
    return { success: false, error: 'Palette name is required' };
  }

  if (name.length > 50) {
    return { success: false, error: 'Palette name must be 50 characters or less' };
  }

  try {
    const db = getDatabase();

    // Check palette count
    const userPalettesRef = ref(db, `palettes/${userId}`);
    const snapshot = await get(userPalettesRef);

    if (snapshot.exists()) {
      const palettesCount = Object.keys(snapshot.val()).length;
      if (palettesCount >= MAX_PALETTES_PER_USER) {
        return {
          success: false,
          error: `Maximum ${MAX_PALETTES_PER_USER} palettes reached. Delete some to save more.`
        };
      }
    }

    // Encode palette to PC2 format
    const paletteCode = encodePaletteForShare(palette);

    if (paletteCode.length > 300) {
      return { success: false, error: 'Palette code exceeds maximum allowed size' };
    }

    // Get first 3 colors for preview (convert to object for Firebase)
    const previewColorsArray = palette.slice(0, 3).map(m => m.hex);
    const previewColors = {};
    previewColorsArray.forEach((color, index) => {
      previewColors[index] = color;
    });

    // Generate new palette ID
    const newPaletteRef = push(userPalettesRef);
    const paletteId = newPaletteRef.key;

    // Save palette
    await set(newPaletteRef, {
      name: name.trim(),
      code: paletteCode,
      colorCount: palette.length,
      previewColors: previewColors,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    return {
      success: true,
      paletteId
    };
  } catch (error) {
    console.error('Failed to save palette:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get all saved palettes for a user
 *
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<{success: boolean, palettes?: Array, error?: string}>}
 */
export async function getUserPalettes(userId) {
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const db = getDatabase();
    const userPalettesRef = ref(db, `palettes/${userId}`);
    const snapshot = await get(userPalettesRef);

    if (!snapshot.exists()) {
      return {
        success: true,
        palettes: []
      };
    }

    const palettesData = snapshot.val();
    const palettes = Object.entries(palettesData).map(([id, data]) => {
      // Convert previewColors object to array
      const previewColors = data.previewColors
        ? Object.values(data.previewColors)
        : [];

      return {
        id,
        ...data,
        previewColors
      };
    });

    // Sort by creation date (newest first)
    palettes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return {
      success: true,
      palettes
    };
  } catch (error) {
    console.error('Failed to get palettes:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get a single palette and decode it
 *
 * @param {string} userId - User's Firebase UID
 * @param {string} paletteId - Palette ID
 * @returns {Promise<{success: boolean, palette?: Object, markers?: Array, error?: string}>}
 */
export async function getPaletteById(userId, paletteId) {
  if (!userId || !paletteId) {
    return { success: false, error: 'Invalid parameters' };
  }

  try {
    const db = getDatabase();
    const paletteRef = ref(db, `palettes/${userId}/${paletteId}`);
    const snapshot = await get(paletteRef);

    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'Palette not found'
      };
    }

    const paletteData = snapshot.val();

    // Decode the palette code to get full marker objects
    const markers = decodePaletteFromShare(paletteData.code);

    return {
      success: true,
      palette: {
        id: paletteId,
        ...paletteData
      },
      markers
    };
  } catch (error) {
    console.error('Failed to get palette:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Delete a saved palette
 *
 * @param {string} userId - User's Firebase UID
 * @param {string} paletteId - Palette ID to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deletePalette(userId, paletteId) {
  if (!userId || !paletteId) {
    return { success: false, error: 'Invalid parameters' };
  }

  try {
    const db = getDatabase();
    const paletteRef = ref(db, `palettes/${userId}/${paletteId}`);
    await remove(paletteRef);

    return { success: true };
  } catch (error) {
    console.error('Failed to delete palette:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update palette name
 *
 * @param {string} userId - User's Firebase UID
 * @param {string} paletteId - Palette ID
 * @param {string} newName - New palette name
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updatePaletteName(userId, paletteId, newName) {
  if (!userId || !paletteId || !newName) {
    return { success: false, error: 'Invalid parameters' };
  }

  if (newName.length > 50) {
    return { success: false, error: 'Palette name must be 50 characters or less' };
  }

  try {
    const db = getDatabase();
    const paletteRef = ref(db, `palettes/${userId}/${paletteId}`);

    // Get existing palette
    const snapshot = await get(paletteRef);
    if (!snapshot.exists()) {
      return { success: false, error: 'Palette not found' };
    }

    await update(paletteRef, {
      name: newName.trim(),
      updatedAt: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Failed to update palette name:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Set or clear the public gallery name for a palette.
 * Pass null/empty string to remove it (falls back to private name in gallery).
 */
export async function updatePalettePublicName(userId, paletteId, publicName) {
  if (!userId || !paletteId) return { success: false, error: 'Invalid parameters' };
  const trimmed = (publicName || '').trim();
  if (trimmed.length > 50) return { success: false, error: 'Public name must be 50 characters or less' };

  try {
    const db = getDatabase();
    const fieldRef = ref(db, `palettes/${userId}/${paletteId}/publicName`);
    if (trimmed) {
      await set(fieldRef, trimmed);
    } else {
      await remove(fieldRef);
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Duplicate a palette (create a copy)
 *
 * @param {string} userId - User's Firebase UID
 * @param {string} paletteId - Palette ID to duplicate
 * @returns {Promise<{success: boolean, newPaletteId?: string, error?: string}>}
 */
export async function duplicatePalette(userId, paletteId) {
  if (!userId || !paletteId) {
    return { success: false, error: 'Invalid parameters' };
  }

  try {
    // Get original palette
    const result = await getPaletteById(userId, paletteId);
    if (!result.success) {
      return result;
    }

    const { palette, markers } = result;

    // Save as new palette with " (Copy)" suffix, truncating to 18 if needed
    const newName = `${palette.name} (Copy)`;
    return await savePalette(userId, markers.slice(0, 20), newName);
  } catch (error) {
    console.error('Failed to duplicate palette:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update the moods string for a palette.
 * Pass null/empty string to remove moods.
 *
 * @param {string} userId
 * @param {string} paletteId
 * @param {string|null} moodsString - dash-separated mood indices e.g. "2-7-14", or null to clear
 */
export async function updatePaletteMoods(userId, paletteId, moodsString) {
  if (!userId || !paletteId) return { success: false, error: 'Invalid parameters' };

  try {
    const db = getDatabase();
    const fieldRef = ref(db, `palettes/${userId}/${paletteId}/moods`);
    if (moodsString) {
      await set(fieldRef, moodsString);
    } else {
      await remove(fieldRef);
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
